import React, { useState, useEffect, useCallback } from 'react';
// Force Vercel redeploy - 29Mar 2026
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../context/AuthContext';
import { projectsAPI, foldersAPI, userAPI } from '../lib/api';
import AppHeader from '../components/AppHeader';
import TrialUpgradeModal from '../components/TrialUpgradeModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Folder, MoreVertical, Trash2, ExternalLink, 
  Circle, LogOut, Crown, Sparkles, Globe, Shield, Rocket, Upload, Target,
  FolderPlus, FolderOpen, ChevronRight, ChevronDown, Edit2, MoveRight, Image, X, BookOpen, HelpCircle
} from 'lucide-react';
import Footer from '../components/Footer';

// Project Card Component (extracted for reuse)
const ProjectCard = ({ project, folders, onDelete, onMove, onRename, navigate, t }) => {
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState(project.name);
  
  const handleRename = () => {
    if (newName.trim() && newName !== project.name) {
      onRename(project.id, newName.trim());
    }
    setRenameDialogOpen(false);
  };
  
  return (
    <Card 
      className="hover:border-primary/50 transition-colors cursor-pointer group"
      data-testid={`project-card-${project.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg">{project.name}</CardTitle>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`project-menu-${project.id}`}
              >
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/workspace/${project.id}`);
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('open_project')}
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setNewName(project.name);
                  setRenameDialogOpen(true);
                }}
                data-testid={`rename-project-${project.id}`}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {t('rename_project')}
              </DropdownMenuItem>
              
              {folders && folders.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onMove(project.id, null);
                    }}
                  >
                    <MoveRight className="w-4 h-4 mr-2" />
                    {t('move_to_root')}
                  </DropdownMenuItem>
                  {folders.map(f => (
                    <DropdownMenuItem 
                      key={f.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMove(project.id, f.id);
                      }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" style={{ color: f.color }} />
                      {t('move_to')} {f.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              
              <DropdownMenuItem 
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(project.id);
                }}
                data-testid={`delete-project-${project.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardDescription className="line-clamp-2">
          {project.description || t('no_description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {project.markers_count ?? project.markers?.length ?? 0} {t('markers')}
          </span>
          <Button 
            size="sm" 
            onClick={() => navigate(`/workspace/${project.id}`)}
            data-testid={`open-project-${project.id}`}
          >
            {t('open_project')}
          </Button>
        </div>
      </CardContent>
      
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t('rename_project')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t('project_name')}</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('project_name')}
                data-testid="rename-project-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={handleRename} data-testid="confirm-rename-btn">
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, changeLanguage } = useAuth();
  const navigate = useNavigate();
  const [langReady, setLangReady] = useState(false);
  
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', folder_id: null });
  const [newFolder, setNewFolder] = useState({ name: '', color: '#6366f1' });
  const [creating, setCreating] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});  // Track which folders are expanded
  const [selectedFolder, setSelectedFolder] = useState(null);  // Currently selected folder filter
  const [moveProjectOpen, setMoveProjectOpen] = useState(null);  // Project ID being moved
  
  // Trial upgrade modal state
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialModalType, setTrialModalType] = useState('trial_project_limit');
  
  // Company Logo state
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    // Sync i18n language with localStorage
    const savedLang = localStorage.getItem('language') || 'en';
    
    const syncLanguage = async () => {
      if (i18n.language !== savedLang) {
        await i18n.changeLanguage(savedLang);
      }
      setLangReady(true);
      
      // Show welcome popup AFTER language is synced
      const justLoggedIn = sessionStorage.getItem('smartballoon_just_logged_in');
      if (justLoggedIn) {
        sessionStorage.removeItem('smartballoon_just_logged_in');
        setShowWelcome(true);
      }
    };
    
    syncLanguage();
    loadProjects();
    loadFolders();
    loadCompanyLogo();
  }, [i18n]);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      // Load only 'projects' type folders for SmartBalloon
      const response = await foldersAPI.list('projects');
      setFolders(response.data || []);
    } catch (error) {
      console.log('Could not load folders:', error);
    }
  };

  // Company Logo functions
  const loadCompanyLogo = async () => {
    try {
      const response = await userAPI.getCompanyLogo();
      if (response.data?.logo) {
        setCompanyLogo(response.data.logo);
      }
    } catch (error) {
      console.log('Could not load company logo:', error);
    }
  };

  const handleLogoUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('logo_only_images'));
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error(t('logo_too_large'));
      return;
    }
    
    setUploadingLogo(true);
    try {
      const response = await userAPI.uploadCompanyLogo(file);
      if (response.data?.logo) {
        setCompanyLogo(response.data.logo);
        toast.success(t('logo_uploaded'));
        setLogoDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await userAPI.deleteCompanyLogo();
      setCompanyLogo(null);
      toast.success(t('logo_deleted'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Logo dropzone configuration
  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps, isDragActive: isLogoDragActive } = useDropzone({
    onDrop: handleLogoUpload,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg']
    },
    maxFiles: 1,
    disabled: uploadingLogo
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await projectsAPI.create({
        ...newProject,
        folder_id: newProject.folder_id || null
      });
      setProjects([response.data, ...projects]);
      setCreateOpen(false);
      setNewProject({ name: '', description: '', folder_id: null });
      toast.success(t('project_created'));
      navigate(`/workspace/${response.data.id}`);
    } catch (error) {
      // Handle trial limit errors
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'trial_project_limit') {
        setCreateOpen(false);
        setTrialModalType('trial_project_limit');
        setTrialModalOpen(true);
      } else if (errorDetail === 'trial_expired') {
        setCreateOpen(false);
        setTrialModalType('trial_expired');
        setTrialModalOpen(true);
      } else {
        toast.error('Failed to create project');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRenameProject = async (projectId, newName) => {
    try {
      await projectsAPI.update(projectId, { name: newName });
      setProjects(projects.map(p => 
        p.id === projectId ? { ...p, name: newName } : p
      ));
      toast.success(t('project_renamed'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolder.name.trim()) return;
    
    try {
      // Create folder with type 'projects' for SmartBalloon
      const response = await foldersAPI.create(newFolder.name, 'projects', null, newFolder.color);
      setFolders([...folders, response.data]);
      setCreateFolderOpen(false);
      setNewFolder({ name: '', color: '#6366f1' });
      toast.success(t('folder_created'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm(t('confirm_delete_folder'))) return;
    
    try {
      await foldersAPI.delete(folderId);
      setFolders(folders.filter(f => f.id !== folderId));
      // Reload projects to update folder_id = null
      loadProjects();
      toast.success(t('folder_deleted'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleMoveProject = async (projectId, folderId) => {
    try {
      await projectsAPI.moveToFolder(projectId, folderId);
      setProjects(projects.map(p => 
        p.id === projectId ? { ...p, folder_id: folderId } : p
      ));
      setMoveProjectOpen(null);
      toast.success(t('project_moved'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const getProjectsInFolder = (folderId) => {
    return projects.filter(p => p.folder_id === folderId);
  };

  const getProjectsWithoutFolder = () => {
    return projects.filter(p => !p.folder_id);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectsAPI.delete(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success(t('project_deleted'));
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getPlanBadge = () => {
    const plan = user?.plan?.toUpperCase() || 'FREE';
    if (plan === 'AI_UNLIMITED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 border border-purple-200">
          <Sparkles className="w-3 h-3" />
          AI Unlimited
        </span>
      );
    }
    if (plan === 'AI') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-amber-100 text-amber-700">
          <Sparkles className="w-3 h-3" />
          AI Premium
        </span>
      );
    }
    if (plan === 'PRO') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
          <Crown className="w-3 h-3" />
          PRO
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-xs font-bold bg-slate-100 text-slate-600">
        FREE
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Welcome Popup - only show when language is ready */}
      {langReady && (
        <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-lg">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl">{i18n.t('welcome_title')}</DialogTitle>
              <DialogDescription className="text-center text-base pt-2">
                {i18n.t('welcome_subtitle')}
              </DialogDescription>
            </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-4 p-3 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{i18n.t('welcome_step1_title')}</p>
                <p className="text-sm text-slate-500">{i18n.t('welcome_step1_desc')}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-3 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Upload className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{i18n.t('welcome_step2_title')}</p>
                <p className="text-sm text-slate-500">{i18n.t('welcome_step2_desc')}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-3 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{i18n.t('welcome_step3_title')}</p>
                <p className="text-sm text-slate-500">{i18n.t('welcome_step3_desc')}</p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowWelcome(false)} 
            className="w-full"
            data-testid="welcome-close-btn"
          >
            {i18n.t('welcome_button')}
          </Button>
        </DialogContent>
      </Dialog>
      )}

      {/* Header - Using shared AppHeader component */}
      <AppHeader />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900">{t('projects')}</h1>
          
          <div className="flex items-center gap-2">
            {/* Company Logo Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setLogoDialogOpen(true)}
              data-testid="company-logo-btn"
              className={companyLogo ? "px-2" : "gap-2"}
            >
              {companyLogo ? (
                <img 
                  src={companyLogo} 
                  alt="Logo Aziendale" 
                  className="h-7 w-auto max-w-[120px] object-contain" 
                />
              ) : (
                <>
                  <Image className="w-4 h-4" />
                  {t('upload_logo')}
                </>
              )}
            </Button>

            {/* New Folder Button */}
            <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="new-folder-btn">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  {t('new_folder')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('new_folder')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateFolder} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{t('folder_name')}</Label>
                    <Input
                      value={newFolder.name}
                      onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                      placeholder={t('folder_placeholder')}
                      required
                      data-testid="folder-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('color') || 'Colore'}</Label>
                    <div className="flex gap-2">
                      {['#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#06b6d4'].map(color => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 ${newFolder.color === color ? 'border-slate-900' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                          onClick={() => setNewFolder({ ...newFolder, color })}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setCreateFolderOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit">{t('create_folder')}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* New Project Button */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('new_project')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('new_project')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="dense-label">{t('project_name')}</Label>
                    <Input
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      placeholder="Part 12345 - Rev A"
                      required
                      className="dense-input"
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="dense-label">{t('project_description')}</Label>
                    <Textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      placeholder="Optional description..."
                      rows={3}
                      data-testid="project-description-input"
                    />
                  </div>
                  {folders.length > 0 && (
                    <div className="space-y-2">
                      <Label>Cartella (opzionale)</Label>
                      <Select 
                        value={newProject.folder_id || 'none'} 
                        onValueChange={(val) => setNewProject({ ...newProject, folder_id: val === 'none' ? null : val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona cartella..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna cartella</SelectItem>
                          {folders.map(f => (
                            <SelectItem key={f.id} value={f.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: f.color }} />
                                {f.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setCreateOpen(false)}
                    >
                      {t('cancel')}
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="create-project-btn">
                      {t('create_project')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">{t('loading')}</div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Folder className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">{t('no_projects')}</p>
              <Button onClick={() => setCreateOpen(true)} data-testid="create-first-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('new_project')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Folders Section */}
            {folders.length > 0 && (
              <div className="space-y-2">
                {folders.map(folder => (
                  <div key={folder.id} className="border rounded-lg bg-white overflow-hidden">
                    {/* Folder Header */}
                    <div 
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleFolder(folder.id)}
                    >
                      <div className="flex items-center gap-2">
                        {expandedFolders[folder.id] ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <FolderOpen className="w-5 h-5" style={{ color: folder.color }} />
                        <span className="font-medium">{folder.name}</span>
                        <span className="text-xs text-slate-400">
                          ({getProjectsInFolder(folder.id).length} progetti)
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button className="p-1 hover:bg-slate-100 rounded">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina cartella
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Projects in Folder */}
                    {expandedFolders[folder.id] && (
                      <div className="border-t bg-slate-50/50 p-3">
                        {getProjectsInFolder(folder.id).length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">
                            Nessun progetto in questa cartella
                          </p>
                        ) : (
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {getProjectsInFolder(folder.id).map(project => (
                              <ProjectCard 
                                key={project.id}
                                project={project}
                                folders={folders}
                                onDelete={handleDelete}
                                onMove={handleMoveProject}
                                onRename={handleRenameProject}
                                navigate={navigate}
                                t={t}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Projects without folder */}
            {getProjectsWithoutFolder().length > 0 && (
              <div className="space-y-2">
                {folders.length > 0 && (
                  <h3 className="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    Senza cartella ({getProjectsWithoutFolder().length})
                  </h3>
                )}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {getProjectsWithoutFolder().map(project => (
                    <ProjectCard 
                      key={project.id}
                      project={project}
                      folders={folders}
                      onDelete={handleDelete}
                      onMove={handleMoveProject}
                      onRename={handleRenameProject}
                      navigate={navigate}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Company Logo Dialog */}
      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Logo Aziendale
            </DialogTitle>
            <DialogDescription>
              Carica il logo della tua azienda. Verrà utilizzato nei report ISIR.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Current Logo Preview */}
            {companyLogo && (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <img 
                    src={companyLogo} 
                    alt="Company Logo" 
                    className="h-12 w-auto object-contain"
                  />
                  <span className="text-sm text-slate-600">Logo attuale</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleDeleteLogo}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid="delete-logo-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Elimina
                </Button>
              </div>
            )}
            
            {/* Upload Dropzone */}
            <div
              {...getLogoRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isLogoDragActive ? 'border-primary bg-primary/5' : 'border-slate-300 hover:border-primary/50'}
                ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              data-testid="logo-dropzone"
            >
              <input {...getLogoInputProps()} />
              <Upload className="w-8 h-8 mx-auto mb-3 text-slate-400" />
              {uploadingLogo ? (
                <p className="text-sm text-slate-500">Caricamento in corso...</p>
              ) : isLogoDragActive ? (
                <p className="text-sm text-primary font-medium">Rilascia qui il logo</p>
              ) : (
                <>
                  <p className="text-sm text-slate-600 font-medium">
                    Trascina il logo qui o clicca per selezionarlo
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    PNG, JPG, SVG • Max 500KB
                  </p>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Footer with Terms/Privacy links */}
      <Footer />
      
      {/* Trial Upgrade Modal */}
      <TrialUpgradeModal 
        isOpen={trialModalOpen}
        onClose={() => setTrialModalOpen(false)}
        type={trialModalType}
      />
    </div>
  );
};

export default DashboardPage;
